import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import type { TripData } from "../types";
import { PlaceDetail } from "./PlaceDetail";
import { pinMayHavePhoto } from "../lib/googlePlaces";
import { computeDayQuest, pinsForDay } from "../lib/questMapState";
import { arrowIcon, makeIcon } from "./mapMarkers";
import "leaflet/dist/leaflet.css";

function FitDayBounds({ pins }: { pins: TripData["pins"] }) {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
    if (pins.length === 0) return;
    const bounds = L.latLngBounds(
      pins.map((p) => [p.lat, p.lng] as [number, number]),
    );
    map.fitBounds(bounds.pad(0.28), { maxZoom: 14 });
  }, [map, pins]);
  return null;
}

/** Pan/zoom to the active quest pin when the schedule selection changes. */
function FocusActivePin({
  lat,
  lng,
  focusKey,
}: {
  lat: number | null;
  lng: number | null;
  focusKey: string;
}) {
  const map = useMap();
  useEffect(() => {
    if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }
    map.invalidateSize();
    const target = L.latLng(lat, lng);
    const z = Math.max(map.getZoom(), 13);
    map.flyTo(target, z, { duration: 0.45 });
  }, [map, lat, lng, focusKey]);
  return null;
}

interface Props {
  data: TripData;
  date: string;
  selectedItemId?: string | null;
  /** Bumps when the user activates a schedule row so focus re-runs. */
  focusKey?: string;
}

export function DayMap({
  data,
  date,
  selectedItemId = null,
  focusKey = "",
}: Props) {
  const dayPins = useMemo(
    () => pinsForDay(data.pins, date),
    [data.pins, date],
  );
  const day = data.days.find((d) => d.date === date);
  const quest = useMemo(
    () => computeDayQuest(day?.items ?? [], data.pins, selectedItemId),
    [day?.items, data.pins, selectedItemId],
  );

  if (dayPins.length === 0) return null;

  const center: [number, number] = [dayPins[0].lat, dayPins[0].lng];
  const active = quest.activePin;

  return (
    <section className="card day-map-card" aria-label="Day map">
      <header className="panel-header">
        <h3>Day map</h3>
        <span className="muted">{dayPins.length} pin{dayPins.length === 1 ? "" : "s"}</span>
      </header>
      <div className="day-map-frame">
        <MapContainer
          center={center}
          zoom={13}
          scrollWheelZoom={false}
          className="leaflet-root day-map-leaflet"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitDayBounds pins={dayPins} />
          <FocusActivePin
            lat={active?.lat ?? null}
            lng={active?.lng ?? null}
            focusKey={focusKey || `${selectedItemId ?? ""}:${date}`}
          />
          {dayPins.map((p) => {
            const visit = quest.pinState.get(p.id) ?? "unvisited";
            return (
              <Marker
                key={`${p.id}-${visit}`}
                title={p.name}
                alt={p.name}
                position={[p.lat, p.lng]}
                icon={makeIcon(p.category, false, visit)}
                zIndexOffset={
                  visit === "active" ? 1000 : visit === "visited" ? 200 : 500
                }
              >
                <Popup>
                  <div className="map-popup">
                    <strong className="map-popup-title">{p.name}</strong>
                    <br />
                    <em className={p.category === "coffee" ? "cat-coffee" : undefined}>
                      {p.category === "coffee" ? "☕ coffee" : p.category}
                    </em>
                    {pinMayHavePhoto(p) && <PlaceDetail pin={p} compact />}
                    {p.notes && (
                      <>
                        <br />
                        {p.notes}
                      </>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
          {quest.arrow && (
            <Marker
              key={`hop-arrow-${quest.arrow.lat.toFixed(5)}-${quest.arrow.lng.toFixed(5)}`}
              position={[quest.arrow.lat, quest.arrow.lng]}
              icon={arrowIcon(quest.arrow.bearing)}
              interactive={false}
              keyboard={false}
              zIndexOffset={1200}
            />
          )}
        </MapContainer>
      </div>
    </section>
  );
}
